import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useStoryDetail } from '@/features/story/use-story-detail';
import { useStoryWorlds } from '@/hooks/useStoryWorlds';
import { patchStoryFavorite } from '@/features/story/api';
import { ApiError, NetworkError } from '@/lib/api-client';
import { StoryErrorBlock } from '@/components/patterns/StoryErrorBlock';
import { storyNotFoundErrorVariant, type VariantHandlers } from '@/features/story/error-variants';
import { getErrorVariant } from '@/lib/errors';
import { StoryHeader, type HeaderWorld } from '@/components/story/StoryHeader';
import { StoryBody } from '@/components/story/StoryBody';
import { StoryAALearningQuestions } from '@/components/story/StoryAALearningQuestions';
import { StorySBKurzerklaert } from '@/components/story/StorySBKurzerklaert';
import { StorySBSachgeschichte } from '@/components/story/StorySBSachgeschichte';
import { StoryAssetFailureBanner } from '@/components/story/StoryAssetFailureBanner';
import { StoryInlineAudioPlayer } from '@/components/story/StoryInlineAudioPlayer';
import { PdfPlaceholderButton } from '@/components/story/PdfPlaceholderButton';
import { RetrofitAssetButton } from '@/components/patterns/RetrofitAssetButton';
import { StoryFooterActions } from '@/components/story/StoryFooterActions';
import { SeriesEpisodeBreadcrumb } from '@/components/story/SeriesEpisodeBreadcrumb';
import {
  useSeriesEpisodeContext,
  type SeriesEpisodeContext,
} from '@/features/series/useSeriesEpisodeContext';
import { ShareBottomSheet } from '@/features/story/share/ShareBottomSheet';
import { resolveStoryMode } from '@/features/story/share/story-mode';
import { RatingWidget } from '@/features/story/rating/RatingWidget';
import { STRINGS } from '@/strings/strings';
import type { PdfStatus, StoryAudioStatus, StoryDetail } from '@/features/story/types';

/**
 * FIX A follow-up — surface-level eligibility gates for the retrofit
 * buttons on StoryRoute. RetrofitAssetButton renders its own pending
 * hint when `currentStatus` is in-flight, which duplicates the large
 * AudioStatusPlaceholder / PdfPlaceholderButton pending blocks on this
 * surface. Wrapping the buttons in these predicates unmounts them the
 * moment the page-level status transitions to pending, leaving the
 * existing placeholders as the single source of truth.
 *
 * SeasonRoute's EpisodeRow keeps RetrofitAssetButton's built-in pending
 * hint — that surface has no other placeholder for retrofit-audio.
 */
const isRetrofitAudioEligible = (s: StoryAudioStatus): boolean =>
  s === 'not_requested' || s === 'failed';
const isRetrofitPdfEligible = (s: PdfStatus): boolean => s === 'not_requested' || s === 'failed';

export function StoryRoute() {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const { story, error, isLoading, setStory } = useStoryDetail(storyId ?? '');
  const { getWorldSlugById } = useStoryWorlds();

  if (isLoading && !story) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-dream-navy" aria-label="Wird geladen…" />
      </div>
    );
  }

  // Batch 12 Sub-phase B — variant-driven error rendering. ApiError
  // carries `errorCode` from the backend envelope; NetworkError
  // (offline / timeout) has no code → falls back to technical via
  // getErrorVariant. story_not_found → Flitzi + Zur-Bibliothek CTA;
  // anything else (network, system_error, unknown) → keeps the
  // existing generic "Geschichte konnte nicht geladen werden" copy
  // for now since it's the briefing-faithful message for non-404
  // load failures.
  if (error || !story) {
    const errorCode = error instanceof ApiError ? error.errorCode : null;
    const variant = getErrorVariant(errorCode);
    if (variant === 'storyNotFound') {
      const handlers: VariantHandlers = {
        onRetry: () => {},
        onGoToLibrary: () => navigate('/library'),
        onChoosePlan: () => {},
        onBuyPack: () => {},
      };
      // Workstream F.6 — pass backend user_message via the hybrid envelope.
      // ApiError.envelope?.user_message wins over the variant title when
      // present. Conditional spread to satisfy exactOptionalPropertyTypes:
      // omit errorEnvelope entirely on NetworkError or when the envelope
      // didn't include a user_message (rather than passing undefined).
      const userMessage = error instanceof ApiError ? error.envelope?.user_message : undefined;
      return (
        <StoryErrorBlock
          {...storyNotFoundErrorVariant(handlers)}
          {...(userMessage ? { errorEnvelope: { user_message: userMessage } } : {})}
          testId="story-error-block"
        />
      );
    }
    return (
      <div className="mx-auto max-w-[560px] px-6 pt-16 pb-12 text-center">
        <p className="text-sm text-dream-navy/80">{STRINGS.storyOutput.page.errors.loadFailed}</p>
      </div>
    );
  }

  const slug = getWorldSlugById(story.worldId);
  const world: HeaderWorld = slug === 'kp' || slug === 'sb' ? slug : 'aa';
  const isExplanation = story.storyType === 'schlauberger_kurz_erklaert';

  return (
    <StoryRouteBody
      story={story}
      world={world}
      isExplanation={isExplanation}
      onFavoriteChange={(next) => setStory(next)}
    />
  );
}

interface BodyProps {
  story: StoryDetail;
  world: HeaderWorld;
  isExplanation: boolean;
  onFavoriteChange: (next: StoryDetail) => void;
  series?: SeriesEpisodeContext;
}

function StoryRouteBody({ story, world, isExplanation, onFavoriteChange }: BodyProps) {
  const [favoriteError, setFavoriteError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Ref target for the Now-Playing "Bewerten" → /story/:id?scrollTo=rating
  // flow. Stable scroll anchor the moment the widget renders.
  const ratingWrapperRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Scroll-to-rating effect — fires once after mount when the URL
  // carries `?scrollTo=rating`. Strips the query param via replaceState
  // so subsequent renders or back-nav don't re-scroll.
  useEffect(() => {
    if (searchParams.get('scrollTo') !== 'rating') return;
    const el = ratingWrapperRef.current;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('scrollTo');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // SI6 — auto-open ShareBottomSheet when URL carries `?action=share`.
  // Target of the share_invitation email's [[share_link]] variable.
  // Same param-strip-on-fire pattern as the scrollTo-rating effect
  // above so refresh / back-nav doesn't re-open the sheet.
  //
  // eslint-disable comment: the React 19 store-prev-prop alternative
  // is incompatible here because react-router-dom v7's
  // setSearchParams must NOT be called during render (it wraps
  // useNavigate, which is render-forbidden). useEffect is the
  // canonical pattern for "react to URL param + fire side-effects +
  // strip param" — the rule's "Avoid setState in effect" warning
  // is too strict for URL-param-driven side-effects.
  useEffect(() => {
    if (searchParams.get('action') !== 'share') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShareOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const shareMode = resolveStoryMode(story.storyType);

  const handleToggleFavorite = async () => {
    const previous = story.isFavorite;
    // Optimistic UI — flip immediately via parent setState, revert on error.
    onFavoriteChange({ ...story, isFavorite: !previous });
    try {
      const next = await patchStoryFavorite(story.id, !previous);
      onFavoriteChange(next);
      setFavoriteError(null);
    } catch (err) {
      onFavoriteChange({ ...story, isFavorite: previous });
      if (err instanceof ApiError || err instanceof NetworkError) {
        setFavoriteError(STRINGS.storyOutput.page.errors.favoriteFailed);
      }
    }
  };

  const handleShareClick = () => {
    setShareOpen(true);
  };

  // B3: series-episode context (breadcrumb + series CTAs). Null for
  // non-series stories or when the feature is dark. Fetches GET /series/:id
  // for the season title + total (absent from the story payload).
  const seriesCtx = useSeriesEpisodeContext(story, world);
  const seriesProp = seriesCtx ? { series: seriesCtx } : {};

  // PWA #16 Sub-Phase 16.3 — per-world Story-Output layouts on the
  // Variante C visual treatment. All 4 worlds covered:
  //   - aa  → AAStoryOutputContent          (Phase 4a)
  //   - kp  → KPStoryOutputContent          (Phase 4b)
  //   - sb !isExplanation → SBSachgeschichteStoryOutputContent (4c)
  //   - sb  isExplanation → SBKurzerklaerungStoryOutputContent (4d)
  // After aa/kp branches return, world is narrowed to 'sb' and the
  // remaining isExplanation check selects between Sach and Kurz.
  if (world === 'aa') {
    return (
      <AAStoryOutputContent
        story={story}
        favoriteError={favoriteError}
        ratingWrapperRef={ratingWrapperRef}
        shareOpen={shareOpen}
        setShareOpen={setShareOpen}
        shareMode={shareMode}
        onToggleFavorite={handleToggleFavorite}
        onShareClick={handleShareClick}
        onFavoriteChange={onFavoriteChange}
        {...seriesProp}
      />
    );
  }

  if (world === 'kp') {
    return (
      <KPStoryOutputContent
        story={story}
        favoriteError={favoriteError}
        ratingWrapperRef={ratingWrapperRef}
        shareOpen={shareOpen}
        setShareOpen={setShareOpen}
        shareMode={shareMode}
        onToggleFavorite={handleToggleFavorite}
        onShareClick={handleShareClick}
        onFavoriteChange={onFavoriteChange}
        {...seriesProp}
      />
    );
  }

  // world === 'sb' below (TS-narrowed via aa/kp eliminations above).
  if (!isExplanation) {
    return (
      <SBSachgeschichteStoryOutputContent
        story={story}
        favoriteError={favoriteError}
        ratingWrapperRef={ratingWrapperRef}
        shareOpen={shareOpen}
        setShareOpen={setShareOpen}
        shareMode={shareMode}
        onToggleFavorite={handleToggleFavorite}
        onShareClick={handleShareClick}
        onFavoriteChange={onFavoriteChange}
        {...seriesProp}
      />
    );
  }

  return (
    <SBKurzerklaerungStoryOutputContent
      story={story}
      favoriteError={favoriteError}
      ratingWrapperRef={ratingWrapperRef}
      shareOpen={shareOpen}
      setShareOpen={setShareOpen}
      shareMode={shareMode}
      onToggleFavorite={handleToggleFavorite}
      onShareClick={handleShareClick}
      onFavoriteChange={onFavoriteChange}
      {...seriesProp}
    />
  );
}

interface AAStoryOutputContentProps {
  story: StoryDetail;
  favoriteError: string | null;
  ratingWrapperRef: React.RefObject<HTMLDivElement | null>;
  shareOpen: boolean;
  setShareOpen: (open: boolean) => void;
  shareMode: ReturnType<typeof resolveStoryMode>;
  onToggleFavorite: () => void;
  onShareClick: () => void;
  onFavoriteChange: (next: StoryDetail) => void;
  series?: SeriesEpisodeContext;
}

/**
 * AA Story-Output layout (PWA #16 Sub-Phase 16.3 Phase 4a). Reuses the
 * AA Builder background composition 1:1: solid bg-world-aa via AppShell
 * Path C is unavailable for `/story/<UUID>` URLs (no world slug), so
 * the route applies bg-world-aa locally and uses the negative-margin
 * trick to extend the bg over AppShell's <main> pb gap. Top-left
 * clouds + airplane corner accent + bottom-right firefighter copied
 * verbatim from AABuilderRoute. Story content lives inside a
 * bg-dream-light-blue rounded-xl card; the Learning-Questions box is
 * nested inside the card (per Sebastian Gate-1 2026-04-29 — Issue 1
 * resolution γ + V2 token verification).
 */
function AAStoryOutputContent({
  story,
  favoriteError,
  ratingWrapperRef,
  shareOpen,
  setShareOpen,
  shareMode,
  onToggleFavorite,
  onShareClick,
  onFavoriteChange,
  series,
}: AAStoryOutputContentProps) {
  const weltLabel = STRINGS.storyOutput.page.weltLabels.aa;
  return (
    <div className="relative -mb-[calc(10rem+env(safe-area-inset-bottom,0))] min-h-screen overflow-hidden bg-world-aa pb-[calc(10rem+env(safe-area-inset-bottom,0))]">
      <div
        className="pointer-events-none absolute top-4 left-4 h-[120px] md:h-[180px] desktop:h-[240px]"
        aria-hidden="true"
      >
        <img src="/worlds/aa/clouds.svg" alt="" className="block h-full w-auto" />
        <img
          src="/worlds/aa/airplane.svg"
          alt=""
          className="absolute top-[15%] left-[35%] h-[55%] w-auto"
        />
      </div>
      <div className="relative mx-auto max-w-[560px] px-5 pt-[152px] pb-12 md:max-w-[768px] md:pt-[212px] desktop:max-w-[1100px] desktop:pt-[272px]">
        {series && <SeriesEpisodeBreadcrumb ctx={series} />}
        <StoryHeader
          title={story.title}
          subtitle={formatStoryDate(story.createdAt)}
          world="aa"
          isFavorite={story.isFavorite}
          onToggleFavorite={onToggleFavorite}
          onShareClick={onShareClick}
          hideTitle
        />

        {favoriteError && (
          <p role="alert" className="mt-2 text-xs text-error">
            {favoriteError}
          </p>
        )}

        <div className="mt-5 rounded-xl bg-dream-light-blue p-5">
          <p className="text-xs font-bold uppercase tracking-[1px] text-dream-navy">{weltLabel}</p>
          <h2 className="mt-3 text-2xl font-bold leading-tight text-dream-navy break-words">
            {story.title}
          </h2>
          <p className="mt-1 text-sm text-dream-navy/60">{formatStoryDate(story.createdAt)}</p>
          <div className="mx-auto mt-4 max-w-[640px]">
            <StoryBody storyText={story.storyText} storyTextPages={story.storyTextPages} />
          </div>
          <div className="mt-6">
            <StoryAALearningQuestions learningQuestions={story.learningQuestions} />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <StoryAssetFailureBanner audioStatus={story.audioStatus} pdfStatus={story.pdfStatus} />
          <StoryInlineAudioPlayer story={story} worldSlug="aa" />
          <PdfPlaceholderButton
            storyId={story.id}
            status={story.pdfStatus}
            storyTitle={story.title}
          />
          {/* FIX A — retrofit triggers. Each button renders ONLY when the
              page-level status is eligible (not_requested/failed); once
              the retrofit POST returns 202 and we flip the status to
              'pending', the button unmounts and the existing large
              placeholders (AudioStatusPlaceholder above,
              PdfPlaceholderButton above) own the pending presentation.
              useStoryDetail polls the story to terminal and updates
              status back so the normal players/PDF affordances appear
              without route re-entry. */}
          {(isRetrofitAudioEligible(story.audioStatus) ||
            isRetrofitPdfEligible(story.pdfStatus)) && (
            <div className="flex flex-wrap gap-2">
              {isRetrofitAudioEligible(story.audioStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="audio"
                  currentStatus={story.audioStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, audioStatus: 'pending' })}
                />
              )}
              {isRetrofitPdfEligible(story.pdfStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="pdf"
                  currentStatus={story.pdfStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, pdfStatus: 'pending' })}
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-6" ref={ratingWrapperRef}>
          <RatingWidget
            storyId={story.id}
            initialRating={story.ratingStars}
            initialComment={story.ratingText}
            initialRatedAt={story.ratedAt}
            onSubmitted={(result) =>
              onFavoriteChange({
                ...story,
                ratingStars: result.ratingStars,
                ratingText: result.ratingText,
                ratedAt: result.ratedAt,
              })
            }
          />
        </div>

        <div className="mt-6">
          <StoryFooterActions
            shareMode={shareMode}
            onShareClick={onShareClick}
            {...(series ? { series } : {})}
          />
        </div>
      </div>

      <div className="relative flex justify-end pr-4 pb-4 md:pr-6 md:pb-6">
        <img
          src="/worlds/aa/firefighter.svg"
          alt=""
          aria-hidden="true"
          className="block h-[120px] w-auto md:h-[180px] desktop:h-[240px]"
        />
      </div>

      <ShareBottomSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        childName={story.childName}
        storyTitle={story.title}
        mode={shareMode}
      />
    </div>
  );
}

interface KPStoryOutputContentProps {
  story: StoryDetail;
  favoriteError: string | null;
  ratingWrapperRef: React.RefObject<HTMLDivElement | null>;
  shareOpen: boolean;
  setShareOpen: (open: boolean) => void;
  shareMode: ReturnType<typeof resolveStoryMode>;
  onToggleFavorite: () => void;
  onShareClick: () => void;
  onFavoriteChange: (next: StoryDetail) => void;
  series?: SeriesEpisodeContext;
}

/**
 * KP Story-Output layout (PWA #16 Sub-Phase 16.3 Phase 4b). Reuses the
 * KP Builder background composition 1:1: bg-world-kp navy via the
 * route div (negative-margin trick to cover AppShell <main> pb gap),
 * stars-overlay.svg as a fixed full-bleed bg layer above the navy
 * paint, and kp-team.svg centered above the story-card. Story content
 * lives inside a bg-dream-light-blue rounded-xl card; KP has no
 * world-specific extra box (Sebastian Gate-1 2026-04-29). Asset
 * banner / audio / pdf / rating / footer stay verbatim from the
 * legacy layout, rendered after the card.
 */
function KPStoryOutputContent({
  story,
  favoriteError,
  ratingWrapperRef,
  shareOpen,
  setShareOpen,
  shareMode,
  onToggleFavorite,
  onShareClick,
  onFavoriteChange,
  series,
}: KPStoryOutputContentProps) {
  const weltLabel = STRINGS.storyOutput.page.weltLabels.kp;
  return (
    <div className="relative -mb-[calc(10rem+env(safe-area-inset-bottom,0))] min-h-screen overflow-hidden bg-world-kp pb-[calc(10rem+env(safe-area-inset-bottom,0))]">
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/worlds/kp/stars-overlay.svg)' }}
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto max-w-[560px] px-5 pt-6 pb-12 md:max-w-[768px] desktop:max-w-[1100px]">
        {series && <SeriesEpisodeBreadcrumb ctx={series} dark />}
        <StoryHeader
          title={story.title}
          subtitle={formatStoryDate(story.createdAt)}
          world="kp"
          isFavorite={story.isFavorite}
          onToggleFavorite={onToggleFavorite}
          onShareClick={onShareClick}
          hideTitle
        />

        {favoriteError && (
          <p role="alert" className="mt-2 text-xs text-error">
            {favoriteError}
          </p>
        )}

        <div className="mt-5 flex justify-center">
          <img
            src="/worlds/kp/kp-team.svg"
            alt=""
            aria-hidden="true"
            className="block h-[140px] w-auto md:h-[200px] desktop:h-[260px]"
          />
        </div>

        <div className="mt-5 rounded-xl bg-dream-light-blue p-5">
          <p className="text-xs font-bold uppercase tracking-[1px] text-dream-navy">{weltLabel}</p>
          <h2 className="mt-3 text-2xl font-bold leading-tight text-dream-navy break-words">
            {story.title}
          </h2>
          <p className="mt-1 text-sm text-dream-navy/60">{formatStoryDate(story.createdAt)}</p>
          <div className="mx-auto mt-4 max-w-[640px]">
            <StoryBody storyText={story.storyText} storyTextPages={story.storyTextPages} />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <StoryAssetFailureBanner audioStatus={story.audioStatus} pdfStatus={story.pdfStatus} />
          <StoryInlineAudioPlayer story={story} worldSlug="kp" />
          <PdfPlaceholderButton
            storyId={story.id}
            status={story.pdfStatus}
            storyTitle={story.title}
          />
          {/* FIX A — retrofit triggers. Each button renders ONLY when the
              page-level status is eligible (not_requested/failed); once
              the retrofit POST returns 202 and we flip the status to
              'pending', the button unmounts and the existing large
              placeholders (AudioStatusPlaceholder above,
              PdfPlaceholderButton above) own the pending presentation.
              useStoryDetail polls the story to terminal and updates
              status back so the normal players/PDF affordances appear
              without route re-entry. */}
          {(isRetrofitAudioEligible(story.audioStatus) ||
            isRetrofitPdfEligible(story.pdfStatus)) && (
            <div className="flex flex-wrap gap-2">
              {isRetrofitAudioEligible(story.audioStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="audio"
                  currentStatus={story.audioStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, audioStatus: 'pending' })}
                />
              )}
              {isRetrofitPdfEligible(story.pdfStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="pdf"
                  currentStatus={story.pdfStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, pdfStatus: 'pending' })}
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-6" ref={ratingWrapperRef}>
          <RatingWidget
            storyId={story.id}
            initialRating={story.ratingStars}
            initialComment={story.ratingText}
            initialRatedAt={story.ratedAt}
            onSubmitted={(result) =>
              onFavoriteChange({
                ...story,
                ratingStars: result.ratingStars,
                ratingText: result.ratingText,
                ratedAt: result.ratedAt,
              })
            }
          />
        </div>

        <div className="mt-6">
          <StoryFooterActions
            shareMode={shareMode}
            onShareClick={onShareClick}
            primaryClassName="border border-dream-white/40"
            secondaryClassName="border-dream-white text-dream-white hover:bg-dream-white/10"
            {...(series ? { series } : {})}
          />
        </div>
      </div>

      <ShareBottomSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        childName={story.childName}
        storyTitle={story.title}
        mode={shareMode}
      />
    </div>
  );
}

interface SBSachgeschichteStoryOutputContentProps {
  story: StoryDetail;
  favoriteError: string | null;
  ratingWrapperRef: React.RefObject<HTMLDivElement | null>;
  shareOpen: boolean;
  setShareOpen: (open: boolean) => void;
  shareMode: ReturnType<typeof resolveStoryMode>;
  onToggleFavorite: () => void;
  onShareClick: () => void;
  onFavoriteChange: (next: StoryDetail) => void;
  series?: SeriesEpisodeContext;
}

/**
 * SB Sachgeschichte Story-Output layout (PWA #16 Sub-Phase 16.3 Phase
 * 4c). Mirrors AA Phase 4a + KP Phase 4b structure: bg-world-sb cream
 * applied directly on the route div (negative-margin trick covers
 * AppShell <main> pb gap), fox.png centered above story-card, story
 * content inside a bg-dream-light-blue rounded-xl card. The Eltern-
 * Info ParentInfoBox is nested INSIDE the card after the body (per
 * Sebastian Gate-1 2026-04-29 — same nested pattern as AA's Learning-
 * Questions box). SB Sachgeschichte has only this one informational
 * box; SB Kurzerklärung (Phase 4d) adds Wow-Fact + Probier-mal.
 *
 * Footer uses default StoryFooterActions styling (no per-route prop
 * overrides) — navy buttons read fine on cream world bg.
 */
function SBSachgeschichteStoryOutputContent({
  story,
  favoriteError,
  ratingWrapperRef,
  shareOpen,
  setShareOpen,
  shareMode,
  onToggleFavorite,
  onShareClick,
  onFavoriteChange,
}: SBSachgeschichteStoryOutputContentProps) {
  const weltLabel = STRINGS.storyOutput.page.weltLabels.sbSachgeschichte;
  return (
    <div className="relative -mb-[calc(10rem+env(safe-area-inset-bottom,0))] min-h-screen overflow-hidden bg-world-sb pb-[calc(10rem+env(safe-area-inset-bottom,0))]">
      <div className="relative mx-auto max-w-[560px] px-5 pt-6 pb-12 md:max-w-[768px] desktop:max-w-[1100px]">
        <StoryHeader
          title={story.title}
          subtitle={formatStoryDate(story.createdAt)}
          world="sb"
          isFavorite={story.isFavorite}
          onToggleFavorite={onToggleFavorite}
          onShareClick={onShareClick}
          hideTitle
        />

        {favoriteError && (
          <p role="alert" className="mt-2 text-xs text-error">
            {favoriteError}
          </p>
        )}

        <div className="mt-5 flex justify-center">
          <img
            src="/worlds/sb/fox.png"
            alt=""
            aria-hidden="true"
            className="block h-[120px] w-auto md:h-[180px] desktop:h-[240px]"
          />
        </div>

        <div className="mt-5 rounded-xl bg-dream-light-blue p-5">
          <p className="text-xs font-bold uppercase tracking-[1px] text-sb-welt-label">
            {weltLabel}
          </p>
          <h2 className="mt-3 text-2xl font-bold leading-tight text-dream-navy break-words">
            {story.title}
          </h2>
          <p className="mt-1 text-sm text-dream-navy/60">{formatStoryDate(story.createdAt)}</p>
          <div className="mx-auto mt-4 max-w-[640px]">
            <StoryBody storyText={story.storyText} storyTextPages={story.storyTextPages} />
          </div>
          <div className="mt-6">
            <StorySBSachgeschichte parentBox={story.parentBox} citations={story.citations} />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <StoryAssetFailureBanner audioStatus={story.audioStatus} pdfStatus={story.pdfStatus} />
          <StoryInlineAudioPlayer story={story} worldSlug="sb" />
          <PdfPlaceholderButton
            storyId={story.id}
            status={story.pdfStatus}
            storyTitle={story.title}
          />
          {/* FIX A — retrofit triggers. Each button renders ONLY when the
              page-level status is eligible (not_requested/failed); once
              the retrofit POST returns 202 and we flip the status to
              'pending', the button unmounts and the existing large
              placeholders (AudioStatusPlaceholder above,
              PdfPlaceholderButton above) own the pending presentation.
              useStoryDetail polls the story to terminal and updates
              status back so the normal players/PDF affordances appear
              without route re-entry. */}
          {(isRetrofitAudioEligible(story.audioStatus) ||
            isRetrofitPdfEligible(story.pdfStatus)) && (
            <div className="flex flex-wrap gap-2">
              {isRetrofitAudioEligible(story.audioStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="audio"
                  currentStatus={story.audioStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, audioStatus: 'pending' })}
                />
              )}
              {isRetrofitPdfEligible(story.pdfStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="pdf"
                  currentStatus={story.pdfStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, pdfStatus: 'pending' })}
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-6" ref={ratingWrapperRef}>
          <RatingWidget
            storyId={story.id}
            initialRating={story.ratingStars}
            initialComment={story.ratingText}
            initialRatedAt={story.ratedAt}
            onSubmitted={(result) =>
              onFavoriteChange({
                ...story,
                ratingStars: result.ratingStars,
                ratingText: result.ratingText,
                ratedAt: result.ratedAt,
              })
            }
          />
        </div>

        <div className="mt-6">
          <StoryFooterActions shareMode={shareMode} onShareClick={onShareClick} />
        </div>
      </div>

      <ShareBottomSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        childName={story.childName}
        storyTitle={story.title}
        mode={shareMode}
      />
    </div>
  );
}

interface SBKurzerklaerungStoryOutputContentProps {
  story: StoryDetail;
  favoriteError: string | null;
  ratingWrapperRef: React.RefObject<HTMLDivElement | null>;
  shareOpen: boolean;
  setShareOpen: (open: boolean) => void;
  shareMode: ReturnType<typeof resolveStoryMode>;
  onToggleFavorite: () => void;
  onShareClick: () => void;
  onFavoriteChange: (next: StoryDetail) => void;
  series?: SeriesEpisodeContext;
}

/**
 * SB Kurzerklärung (Wissensblatt) Story-Output layout (PWA #16
 * Sub-Phase 16.3 Phase 4d). Structurally identical to SB
 * Sachgeschichte (Phase 4c) — same cream bg, fox.png centered, same
 * card with Welt-Label/Title/Date/Body — but renders the 3-box
 * StorySBKurzerklaert block (Wow-Fact + Probier-mal + Eltern-Info)
 * inside the card after the body, instead of just the single Eltern-
 * Info ParentInfoBox. Welt-Label string differs ("💡 Schlauberger
 * Wissensblatt").
 *
 * D1 responsive: Wow + Probier side-by-side at md:; Eltern-Info
 * always full-width below. Encapsulated inside StorySBKurzerklaert.
 */
function SBKurzerklaerungStoryOutputContent({
  story,
  favoriteError,
  ratingWrapperRef,
  shareOpen,
  setShareOpen,
  shareMode,
  onToggleFavorite,
  onShareClick,
  onFavoriteChange,
}: SBKurzerklaerungStoryOutputContentProps) {
  const weltLabel = STRINGS.storyOutput.page.weltLabels.sbKurzerklaerung;
  return (
    <div className="relative -mb-[calc(10rem+env(safe-area-inset-bottom,0))] min-h-screen overflow-hidden bg-world-sb pb-[calc(10rem+env(safe-area-inset-bottom,0))]">
      <div className="relative mx-auto max-w-[560px] px-5 pt-6 pb-12 md:max-w-[768px] desktop:max-w-[1100px]">
        <StoryHeader
          title={story.title}
          subtitle={formatStoryDate(story.createdAt)}
          world="sb"
          isFavorite={story.isFavorite}
          onToggleFavorite={onToggleFavorite}
          onShareClick={onShareClick}
          hideTitle
        />

        {favoriteError && (
          <p role="alert" className="mt-2 text-xs text-error">
            {favoriteError}
          </p>
        )}

        <div className="mt-5 flex justify-center">
          <img
            src="/worlds/sb/fox.png"
            alt=""
            aria-hidden="true"
            className="block h-[120px] w-auto md:h-[180px] desktop:h-[240px]"
          />
        </div>

        <div className="mt-5 rounded-xl bg-dream-light-blue p-5">
          <p className="text-xs font-bold uppercase tracking-[1px] text-sb-welt-label">
            {weltLabel}
          </p>
          <h2 className="mt-3 text-2xl font-bold leading-tight text-dream-navy break-words">
            {story.title}
          </h2>
          <p className="mt-1 text-sm text-dream-navy/60">{formatStoryDate(story.createdAt)}</p>
          <div className="mx-auto mt-4 max-w-[640px]">
            <StoryBody storyText={story.storyText} storyTextPages={story.storyTextPages} />
          </div>
          <div className="mt-6">
            <StorySBKurzerklaert
              wowFact={story.wowFact}
              tryThis={story.tryThis}
              parentBox={story.parentBox}
              citations={story.citations}
            />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <StoryAssetFailureBanner audioStatus={story.audioStatus} pdfStatus={story.pdfStatus} />
          <StoryInlineAudioPlayer story={story} worldSlug="sb" />
          <PdfPlaceholderButton
            storyId={story.id}
            status={story.pdfStatus}
            storyTitle={story.title}
          />
          {/* FIX A — retrofit triggers. Each button renders ONLY when the
              page-level status is eligible (not_requested/failed); once
              the retrofit POST returns 202 and we flip the status to
              'pending', the button unmounts and the existing large
              placeholders (AudioStatusPlaceholder above,
              PdfPlaceholderButton above) own the pending presentation.
              useStoryDetail polls the story to terminal and updates
              status back so the normal players/PDF affordances appear
              without route re-entry. */}
          {(isRetrofitAudioEligible(story.audioStatus) ||
            isRetrofitPdfEligible(story.pdfStatus)) && (
            <div className="flex flex-wrap gap-2">
              {isRetrofitAudioEligible(story.audioStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="audio"
                  currentStatus={story.audioStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, audioStatus: 'pending' })}
                />
              )}
              {isRetrofitPdfEligible(story.pdfStatus) && (
                <RetrofitAssetButton
                  storyId={story.id}
                  variant="pdf"
                  currentStatus={story.pdfStatus}
                  onEnqueued={() => onFavoriteChange({ ...story, pdfStatus: 'pending' })}
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-6" ref={ratingWrapperRef}>
          <RatingWidget
            storyId={story.id}
            initialRating={story.ratingStars}
            initialComment={story.ratingText}
            initialRatedAt={story.ratedAt}
            onSubmitted={(result) =>
              onFavoriteChange({
                ...story,
                ratingStars: result.ratingStars,
                ratingText: result.ratingText,
                ratedAt: result.ratedAt,
              })
            }
          />
        </div>

        <div className="mt-6">
          <StoryFooterActions shareMode={shareMode} onShareClick={onShareClick} />
        </div>
      </div>

      <ShareBottomSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        childName={story.childName}
        storyTitle={story.title}
        mode={shareMode}
      />
    </div>
  );
}

function formatStoryDate(createdAtIso: string): string {
  const today = STRINGS.storyOutput.page.meta.today;
  const created = new Date(createdAtIso);
  if (Number.isNaN(created.getTime())) return today;

  const now = new Date();
  const isSameDay =
    created.getFullYear() === now.getFullYear() &&
    created.getMonth() === now.getMonth() &&
    created.getDate() === now.getDate();
  if (isSameDay) return today;

  const d = String(created.getDate()).padStart(2, '0');
  const m = String(created.getMonth() + 1).padStart(2, '0');
  return `${d}.${m}.${created.getFullYear()}`;
}
